import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function PayrollPDFDialog({
  open,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  formErrors,
  isSubmitting = false,
}) {
  const exportLabel = formData.file_type === 'SalarySlip' ? 'Salary Slip' : formData.file_type || 'PDF';
  const isSalarySlip = formData.file_type === 'SalarySlip';
  const shouldUpdatePaymentDate = !!formData.update_payment_date;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Generate Payroll File
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Select month, year, and file type for payroll export
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.month}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={formData.month || ''}
                  label="Month"
                  onChange={(e) => onInputChange('month', e.target.value)}
                  required
                >
                  {MONTHS.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.month && (
                  <Typography variant="caption" color="error">
                    {formErrors.month}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.year}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.year || ''}
                  label="Year"
                  onChange={(e) => onInputChange('year', e.target.value)}
                  required
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.year && (
                  <Typography variant="caption" color="error">
                    {formErrors.year}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.file_type}>
                <InputLabel>File Type</InputLabel>
                <Select
                  value={formData.file_type || 'PDF'}
                  label="File Type"
                  onChange={(e) => onInputChange('file_type', e.target.value)}
                  required
                >
                  <MenuItem value="SalarySlip">Salary Slip (PDF / ZIP)</MenuItem>
                  <MenuItem value="PDF">PDF</MenuItem>
                  <MenuItem value="Excel">Excel</MenuItem>
                </Select>
                {formErrors.file_type && (
                  <Typography variant="caption" color="error">
                    {formErrors.file_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {!isSalarySlip && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shouldUpdatePaymentDate}
                      onChange={(e) => onInputChange('update_payment_date', e.target.checked)}
                    />
                  }
                  label="Update payment date"
                />
              </Grid>
            )}

            {shouldUpdatePaymentDate && (
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ width: '100%' }}>
                    <DatePicker
                      label="Payment Date"
                      value={formData.payment_date}
                      onChange={(value) => onInputChange('payment_date', value)}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          required
                          error={!!formErrors.payment_date}
                          helperText={formErrors.payment_date}
                          sx={{ width: '100%' }}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
            )}

            {!isSalarySlip && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!formData.create_expense_entry}
                      onChange={(e) => onInputChange('create_expense_entry', e.target.checked)}
                    />
                  }
                  label="Create expense entry for this payroll"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Generating...' : `Generate ${exportLabel}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

PayrollPDFDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    month: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    file_type: PropTypes.oneOf(['PDF', 'Excel', 'SalarySlip']),
    payment_date: PropTypes.instanceOf(Date),
    update_payment_date: PropTypes.bool,
    create_expense_entry: PropTypes.bool,
  }).isRequired,
  onInputChange: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    month: PropTypes.string,
    year: PropTypes.string,
    file_type: PropTypes.string,
    payment_date: PropTypes.string,
  }),
  isSubmitting: PropTypes.bool,
};

PayrollPDFDialog.defaultProps = {
  formErrors: {},
  isSubmitting: false,
};

export default PayrollPDFDialog;
